import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useNavigate } from "react-router-dom";

import { useFeatureFlag } from "@/services/Flags";
import { get } from "api";
import type { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { getCustomCategoryDestinationUrl } from "toolbox/CustomCategories";

export interface PromotionHeroProps extends Models<"SiteWidePromo"> {
  onClick?: () => void;
  categoryId: string;
  homeStyle?: boolean;
  giftCardListings?: Models<"DisplayGiftCardListing">[];
}

const PromotionHero = (props: PromotionHeroProps) => {
  const navigate = useNavigate();
  const isGiftCardsEnabled = useFeatureFlag("giftcards");
  const { data: customShopCategories } = useQuery({
    queryKey: ["/shop/custom-categories/listings"],
    queryFn: () => get("/shop/custom-categories/listings"),
    staleTime: 1000 * 10,
  });
  const hasCustomShopCategoriesWithGiftCards =
    isGiftCardsEnabled &&
    customShopCategories?.some((csc) =>
      csc.categoryInclusions.some(
        (ci) =>
          ci.packageListingId &&
          props.giftCardListings?.some((gc) => gc.id === ci.packageListingId)
      )
    );

  let allCustomShopCategoriesAreGiftCards = false;
  if (hasCustomShopCategoriesWithGiftCards) {
    allCustomShopCategoriesAreGiftCards = true;
    for (const csc of customShopCategories ?? []) {
      if (!csc.promotion) continue;
      for (const ci of csc.categoryInclusions) {
        if (
          ci.packageListingId &&
          !props.giftCardListings?.find((gc) => gc.id === ci.packageListingId)
        ) {
          allCustomShopCategoriesAreGiftCards = false;
          console.log(
            "allCustomShopCategoriesAreGiftCards",
            allCustomShopCategoriesAreGiftCards
          );
          break;
        }
      }
    }
  }

  const customShopCategory = customShopCategories?.find(
    (result) => result.category.id === props.categoryId
  );

  const customShopCategoryTo = allCustomShopCategoriesAreGiftCards
    ? "/shop/gift-cards"
    : customShopCategory
    ? getCustomCategoryDestinationUrl(
        customShopCategory,
        hasCustomShopCategoriesWithGiftCards
      )
    : `/shop/custom-category/${props.categoryId}`;

  const handleClick = () => {
    navigate(customShopCategoryTo);
  };

  return (
    <div
      onClick={handleClick}
      className={cx(
        "flex flex-col items-center bg-cover bg-center text-white",
        !props.homeStyle ? "h-[360px]" : "-mt-3 aspect-[390/434] p-8"
      )}
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.2) 12%, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0) 55%, rgba(0, 0, 0, 0.25) 80%), url(${props.imageUrl})`,
      }}
    >
      <div className="mt-10 text-center font-serif text-display text-shadow">
        {props.title}
      </div>

      {props.description && (
        <div className="sticky top-[244px] text-center text-body2 text-shadow">
          {props.description}
        </div>
      )}
      {props.categoryId !== null && props.callToActionText && (
        <div className="sticky top-[278px] min-w-[158px] whitespace-nowrap text-center">
          <Button style="outline" size="medium" fullWidth>
            {props.callToActionText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PromotionHero;
